'use client';

import { Github } from 'lucide-react';

import { Button, Card } from '@/components/ui';
import { GITHUB_APP_INSTALL_URL } from '@/lib/constants';
import { theme } from '@/lib/ui/theme';

interface InstallGithubAppProps {
  /** Active organization name, to personalize the heading ("Finish setting up <name>"). */
  organizationName?: string;
}

/**
 * InstallGithubApp is the first setup step for an organization: install the GitHub App so
 * Mindrealm can review pull requests. We only support GitHub today, so the name is
 * specific. Narrow, centered setup card (icon + heading + action); installing the app
 * starts the 7-day trial and routes into onboarding.
 */
export function InstallGithubApp({ organizationName }: InstallGithubAppProps) {
  const handleGitHubInstall = () => {
    window.location.href = GITHUB_APP_INSTALL_URL;
  };

  return (
    <Card className="border-primary/20 mx-auto max-w-md">
      <div className="flex flex-col items-center gap-4 py-2 text-center">
        <div className="bg-primary/10 text-primary flex h-12 w-12 items-center justify-center rounded-full">
          <Github className="h-6 w-6" />
        </div>
        <div className="space-y-1.5">
          <h2
            className="text-xl font-semibold"
            style={{ fontFamily: theme.fonts.body }}
          >
            {organizationName
              ? `Finish setting up ${organizationName}`
              : 'Connect your code'}
          </h2>
          <p
            className="text-muted-foreground text-sm"
            style={{ fontFamily: theme.fonts.body }}
          >
            Install the GitHub App so Mindrealm can review your pull requests
            automatically and catch issues before they merge. This is the first
            step to get your organization running.
          </p>
        </div>
        <div className="flex flex-col items-center gap-1.5">
          <Button onClick={handleGitHubInstall}>
            <Github className="mr-2 h-4 w-4" />
            Install GitHub App
          </Button>
          <p
            className="text-muted-foreground text-xs"
            style={{ fontFamily: theme.fonts.body }}
          >
            Starts your 7-day free trial
          </p>
        </div>
      </div>
    </Card>
  );
}
